import { useAuth } from '../contexts/AuthContext';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasRole,
  hasAnyRole,
  isSuperAdmin,
  isFarmAdmin,
  hasAccessToFarm,
  getAccessibleFarmIds,
} from '../utils/permissions';

/**
 * Custom hook for permission checking
 * Provides easy access to permission utilities with the current user
 */
export const usePermissions = () => {
  const { user } = useAuth();

  return {
    // User info
    user,
    isSuperAdmin: isSuperAdmin(user),
    isFarmAdmin: isFarmAdmin(user),
    
    // Permission checks
    can: (permission) => hasPermission(user, permission),
    canAny: (permissions) => hasAnyPermission(user, permissions),
    canAll: (permissions) => hasAllPermissions(user, permissions),
    
    // Role checks
    hasRole: (role) => hasRole(user, role),
    hasAnyRole: (roles) => hasAnyRole(user, roles),
    
    // Farm access
    canAccessFarm: (farmId) => hasAccessToFarm(user, farmId),
    accessibleFarmIds: getAccessibleFarmIds(user),
    farms: user?.farms || [],
    
    // Shortcuts for common permissions
    canViewDashboard: hasPermission(user, 'view dashboard'),
    canViewFarms: hasPermission(user, 'view farms'),
    canManageFarms: hasPermission(user, 'edit farms'),
    canViewAnimals: hasPermission(user, 'view animals'),
    canManageAnimals: hasPermission(user, 'edit animals'),
    canViewReports: hasPermission(user, 'view reports'),
    canViewUsers: hasPermission(user, 'view users'),
    canManageUsers: hasAnyPermission(user, ['create users', 'edit users']),
    canManageRoles: hasPermission(user, 'manage roles'),
    canViewMovements: hasPermission(user, 'view animal movements'),
    canCreateMovements: hasPermission(user, 'create animal movements'),
    canDeleteMovements: hasPermission(user, 'delete animal movements'),
  };
};

export default usePermissions;

